import { useEffect, useId, useMemo, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import {
    Combobox,
    TagsInput,
    useCombobox,
    useFilter,
    useListCollection,
    useTagsInput,
} from "@chakra-ui/react"
import { getTags } from "../../api/tags"
import { TagsFieldProps } from "../../api/search"


const TagsField = ({ value, onChange }: TagsFieldProps) => {
    const { data: tagsList } = useQuery({...getTags(0)})
    const { contains } = useFilter({ sensitivity: "base" })

    const { collection, filter, reset } = useListCollection({
        initialItems: tagsList?.data?.map(c => c.name) ?? [],
        filter: contains,
    })

    const tagIdMap = useMemo(() => {
        const map = new Map<string, string>()
        tagsList?.data?.forEach(t => map.set(t.name, t.id))
        return map
    }, [tagsList])

    useEffect(() => {
        if (tagsList?.data) reset()
    }, [tagsList])

    const uid = useId()
    const controlRef = useRef<HTMLDivElement | null>(null)

    const tags = useTagsInput({
        ids: { input: `input_${uid}`, control: `control_${uid}` },
        max: 1,
        value: value ? [value] : [],
        onValueChange: (e) => {
            const name = e.value[0] ?? null
            const id = name ? tagIdMap.get(name) ?? null : null
            onChange(id, name)
        }
    })

    const combobox = useCombobox({
        ids: { input: `input_${uid}`, control: `control_${uid}` },
        collection,
        onInputValueChange(e) {
            filter(e.inputValue)
        },
        value: [],
        allowCustomValue: true,
        onValueChange: (e) => {
            const name = e.value[0]
            const id = tagIdMap.get(name) ?? null
            onChange(id, name)
        },
        selectionBehavior: "clear",
    })

    return (
        <Combobox.RootProvider value={combobox}>
        <TagsInput.RootProvider value={tags}>
            <TagsInput.Label fontSize={16}>Tag</TagsInput.Label>

            <TagsInput.Control ref={controlRef}>
            {tags.value.map((tag, index) => (
                <TagsInput.Item key={index} index={index} value={tag}>
                <TagsInput.ItemPreview bgColor="main" color="white">
                    <TagsInput.ItemText>{tag}</TagsInput.ItemText>
                    <TagsInput.ItemDeleteTrigger />
                </TagsInput.ItemPreview>
                </TagsInput.Item>
            ))}

            <Combobox.Input unstyled asChild>
                <TagsInput.Input
                    placeholder={tags.value.length === 0 ? "Tag hinzufügen..." : ""}
                    onFocus={() => {
                        combobox.setOpen(true)
                    }}
                />
            </Combobox.Input>
            </TagsInput.Control>

            <Combobox.Positioner>
            <Combobox.Content>
                {collection.items.map((item) => (
                <Combobox.Item item={item} key={item}>
                    <Combobox.ItemText>{item}</Combobox.ItemText>
                    <Combobox.ItemIndicator />
                </Combobox.Item>
                ))}
            </Combobox.Content>
            </Combobox.Positioner>
        </TagsInput.RootProvider>
        </Combobox.RootProvider>
    )

}

export default TagsField